import { Box, Typography } from '@mui/material';
import React, { ReactElement } from 'react';
import type { OrganizationAttendee } from 'services/api';
import { formatPeopleDate } from '../formatPeopleDate';
import { AttendeeRolePill } from './AttendeeRolePill';
import { EngagementPill } from './EngagementPill';

const COL_NAME = 300;
const COL_ROLE = 100;
const COL_LAST = 122;
const COL_RSVP = 100;
const COL_ATT = 100;
const COL_ENG = 100;
const GAP = 48;

const TEXT_COLOR = '#383838';
const HEADER_BORDER = '#E5E7EB';
const ROW_BORDER = '#DEDEDE';

function HeaderCell ({
  children,
  width,
  align = 'flex-start',
}: {
  children: React.ReactNode;
  width: number;
  align?: 'flex-start' | 'center';
}): ReactElement {
  return (
    <Box
      sx={{
        width,
        flexShrink: 0,
        py: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: align,
      }}
    >
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '18px',
          textTransform: 'uppercase',
          color: TEXT_COLOR,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function StatCell ({
  count,
  total,
  percent,
}: {
  count: number;
  total: number;
  percent: number;
}): ReactElement {
  return (
    <Box
      sx={{
        width: COL_RSVP,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '21px',
          color: TEXT_COLOR,
          textAlign: 'center',
        }}
      >
        {count}/{total}
      </Typography>
      <Typography
        sx={{
          fontFamily: '\'Poppins\', sans-serif',
          fontSize: '12px',
          fontWeight: 600,
          lineHeight: '18px',
          color: TEXT_COLOR,
          textAlign: 'center',
        }}
      >
        {Math.round(percent)}%
      </Typography>
    </Box>
  );
}

export function AttendeesDesktopTable ({
  attendees,
}: {
  attendees: OrganizationAttendee[];
}): ReactElement {
  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <Box
        display="flex"
        alignItems="center"
        gap={`${GAP}px`}
        sx={{
          height: 50,
          borderBottom: `1px solid ${HEADER_BORDER}`,
        }}
      >
        <HeaderCell width={COL_NAME}>Name</HeaderCell>
        <HeaderCell width={COL_ROLE}>Role</HeaderCell>
        <HeaderCell width={COL_LAST}>Last Attended</HeaderCell>
        <HeaderCell width={COL_RSVP} align="center">RSVPs</HeaderCell>
        <HeaderCell width={COL_ATT} align="center">Attended</HeaderCell>
        <HeaderCell width={COL_ENG} align="center">Engagement</HeaderCell>
      </Box>

      {attendees.map(a => {
        const rsvpPercent = a.totalEvents > 0 ? (a.rsvpCount / a.totalEvents) * 100 : 0;
        return (
          <Box
            key={a.userId}
            display="flex"
            alignItems="center"
            gap={`${GAP}px`}
            sx={{
              py: '16px',
              borderBottom: `1px solid ${ROW_BORDER}`,
              '&:last-of-type': { borderBottom: 'none' },
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#FAFAFA' },
            }}
          >
            <Box sx={{ width: COL_NAME, flexShrink: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '24px',
                  color: TEXT_COLOR,
                  whiteSpace: 'nowrap',
                }}
              >
                {a.firstName} {a.lastName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: TEXT_COLOR,
                }}
              >
                {a.schoolEmail}
              </Typography>
            </Box>

            <Box sx={{ width: COL_ROLE, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {a.role ? (
                <AttendeeRolePill role={a.role} />
              ) : (
                <Typography
                  sx={{
                    fontFamily: '\'Poppins\', sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '21px',
                    color: TEXT_COLOR,
                    textAlign: 'center',
                  }}
                >
                  —
                </Typography>
              )}
            </Box>

            <Box sx={{ width: COL_LAST, flexShrink: 0 }}>
              <Typography
                sx={{
                  fontFamily: '\'Poppins\', sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '21px',
                  color: TEXT_COLOR,
                  whiteSpace: 'nowrap',
                }}
              >
                {formatPeopleDate(a.lastAttended)}
              </Typography>
            </Box>

            <StatCell count={a.rsvpCount} total={a.totalEvents} percent={rsvpPercent} />
            <StatCell count={a.attendedCount} total={a.totalEvents} percent={a.attendedPercent} />

            <Box sx={{ width: COL_ENG, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EngagementPill engagement={a.engagement} fullWidth />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
